import { Link } from "react-router-dom";

import { OfferCard } from "@sill/shared/components/marketplace/offer-card";

import type { OfferForBuyer } from "../../lib/marketplace-offers-api";
import { SectionLabel } from "../marketplace/OffersInbox";

export interface OffersTeaserProps {
  /** The top made-for-you offer, or null when nothing was made for the buyer. */
  offer: OfferForBuyer | null;
  /** Every offer in the inbox (made-for-you + standing), for "See all N". */
  inboxTotal: number;
  onOfferTeaserClicked?: () => void;
}

/**
 * Plan 435 M3 (mfh-offers-teaser). The top made-for-you `OfferCard` under the
 * SAME `OffersInbox.SectionLabel` treatment the Your Offers page uses, with a
 * "See all N →" deep link to `/offers`. Hidden entirely when nothing was made
 * for the buyer — this block is a teaser into the real inbox, never a second
 * empty-state surface.
 *
 * Standing offers are NOT rendered here (plan 444 follow-up, #710 operator
 * decision). Browse is tier-filtered to agents with a standing offer at the
 * buyer's tier, so every standing match is the same agent `AgentPreview`
 * already shows as a tile (whose savings figure comes from that standing
 * offer); a second, thinner row for it showed one agent twice at two sizes.
 * The inbox still lists them, and "See all N" counts them.
 */
export function OffersTeaser({
  offer: topOffer,
  inboxTotal,
  onOfferTeaserClicked,
}: OffersTeaserProps) {
  if (!topOffer) return null;

  return (
    <section className="space-y-3" data-testid="home-offers-teaser">
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <SectionLabel label="Offers for you" />
        </div>
        <Link
          className="shrink-0 text-xs font-bold text-primary"
          data-testid="home-offers-teaser-see-all"
          to="/offers"
        >
          See all {inboxTotal} →
        </Link>
      </div>
      <Link
        className="block"
        data-testid="home-offer-teaser-active"
        onClick={() => onOfferTeaserClicked?.()}
        to={topOffer.offer_id ? `/offers/${topOffer.offer_id}` : "/offers"}
      >
        <OfferCard
          data={{
            commissionPct: topOffer.commission_pct,
            chrome: topOffer.own_agent ? "own-agent" : undefined,
            deliveryState: topOffer.delivery_state,
            expiresAt: topOffer.expires_at,
            items: topOffer.items,
            note: topOffer.note,
            proBrokerage: topOffer.pro_brokerage,
            proDisplayName: topOffer.pro_display_name,
            proPhotoUrl: topOffer.pro_photo_url,
            actionLabel: "View offer",
          }}
        />
      </Link>
    </section>
  );
}
